// src/domains/table.ts —— 06 A-00 通用表格协议原生实现（TD-820 服务端分页 / facet / 排序白名单 / 请求串回显；TD-821 服务端 CSV）
// 每个 host × 表格页各持有一个 TableState；会话边界（session:lost / session:ready）整组丢弃，旧实例的在途结果不再写入（跨账号不串数据）
import type { AdminPage } from '../../../shared/src/api/types';
import { csvRowCount, fallbackCsvName, saveBlob } from '../../../shared/src/download';
import { listQuery, type ListQuery } from '../../../shared/src/page';
import { TableState } from '../../../shared/src/table';
import { todayIn } from '../../../shared/src/time';
import { I18N_LISTS, REWRITES_LISTS } from '../i18n/lists';
import { PAGES, isTablePage } from '../pages/index';
import type { AnyPage, DetailSpec, TablePage } from '../pages/types';
import type { AgentState } from '../state';
import { act, btn, cell, chip, field, li, pageBtnStyle, pagerStyle, statCard, tabStyle, thStyle } from '../ui';
import { loadOverview } from './home';
import { KEYS, MODAL_KEYS } from './table.keys';
import { str, strs, type AgentCtx, type AgentDomain, type InputEvent } from './types';

export interface TableSlice {
  rev: number; // TableState 为可变对象，状态变化以 rev 递增触发宿主重渲染
  detail: DetailSpec | null;
}

declare module '../state' {
  interface AgentSlices {
    tb?: TableSlice;
  }
}

const TB0: TableSlice = { rev: 0, detail: null };

export function tbOf(s: AgentState): TableSlice {
  return s.tb ?? TB0;
}

interface HostTables {
  readonly map: Map<TablePage, TableState<unknown>>;
  exporting: boolean;
}

const perHost = new WeakMap<object, HostTables>();
const lastGood = new WeakMap<TableState<unknown>, AdminPage<unknown>>();

function tablesOf(ctx: AgentCtx): HostTables {
  let h = perHost.get(ctx.host);
  if (!h) {
    h = { map: new Map(), exporting: false };
    perHost.set(ctx.host, h);
  }
  return h;
}

// 在途结果判定：发请求时的会话代（HostTables 实例）在写入前已被 session:lost 替换即丢弃。
// 不按 token 比较：同一会话内 401 → refresh 会换发 access token，按 token 判定会误丢合法结果
function sameSession(ctx: AgentCtx, h: HostTables): boolean {
  return perHost.get(ctx.host) === h;
}

// 旧会话实例已不可达（tablesOf 返回新 HostTables），回空页只为让其 reload 正常收尾，不落日志 / toast
function stalePage(q: ListQuery): AdminPage<unknown> {
  return { list: [], total: 0, page: q.page, size: q.size };
}

// 丢弃本 host 全部 TableState（在途结果按会话代丢弃）并复位切片；幂等
function dropSession(ctx: AgentCtx): void {
  perHost.delete(ctx.host);
  const tb = tbOf(ctx.host.state);
  if (tb.detail !== null || tb.rev !== 0) ctx.host.setState({ tb: { ...TB0 } });
}

function bump(ctx: AgentCtx): void {
  const tb = tbOf(ctx.host.state);
  ctx.host.setState({ tb: { ...tb, rev: tb.rev + 1 } });
}

// S-64：下级页页签改发 facet status=（空 / active / disabled），不带 tab
export function toQuery(def: AnyPage, q: ListQuery): Record<string, string | number> {
  const base = def.query ? def.query(q) : q;
  if (def.tabParam === 'status') {
    const { tab, ...rest } = base;
    return listQuery({ ...rest, facets: { ...(rest.facets ?? {}), status: tab ?? '' } });
  }
  return listQuery(base);
}

// M-6：回显串与实际下发参数同源（含 tab / sort / order；下级页为 status）
export function echoLine(def: AnyPage, q: ListQuery): string {
  const qs = Object.entries(toQuery(def, q)).map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return `GET ${def.echo}?${qs.join('&')}`;
}

export function tableOf(ctx: AgentCtx, page: TablePage): TableState<unknown> {
  const h = tablesOf(ctx);
  const hit = h.map.get(page);
  if (hit) return hit;
  const def = PAGES[page];
  const ts: TableState<unknown> = new TableState<unknown>({
    endpoint: def.echo,
    minLoadingMs: 400,
    searchingMs: 520,
    fetch: async (q) => {
      try {
        const r = await ctx.gp.api.call<AdminPage<unknown>>(def.route, { query: toQuery(def, q) });
        lastGood.set(ts, r);
        return r;
      } catch (e) {
        // 会话已换：旧实例不可达（成功结果写入旧实例无害），失败不得在新会话下 toast
        if (!sameSession(ctx, h)) return stalePage(q);
        // A-00：网络 / 5xx → toast + 保留旧数据
        ctx.gp.ui.toast(ctx.gp.ui.err(e));
        const prev = lastGood.get(ts);
        if (prev) return prev;
        throw e;
      }
    },
    onChange: () => {
      if (sameSession(ctx, h)) bump(ctx);
    },
  });
  ts.tab = def.tabs[0] ?? '';
  h.map.set(page, ts);
  return ts;
}

// 导航切换 / 页签切换：tab 回指定键；工具条 / 请求串 / 排序清空；page=1（A-00）
function resetTo(ts: TableState<unknown>, tab: string): Promise<void> {
  ts.sortKey = null;
  ts.sortDir = 'desc';
  return ts.setTab(tab);
}

export async function reloadPage(ctx: AgentCtx, page: TablePage): Promise<void> {
  await tableOf(ctx, page).reload();
}

// TD-821 / 06 A-00 导出：同查询参数 + format=csv，服务端生成；1504 / 5000 / 网络 → errorText agent 表（errExportLimit / toastNetwork）
async function exportCsv(ctx: AgentCtx, page: TablePage): Promise<void> {
  const h = tablesOf(ctx);
  if (h.exporting) return;
  const def = PAGES[page];
  const ts = tableOf(ctx, page);
  h.exporting = true;
  try {
    const { blob, filename } = await ctx.gp.api.blob(def.route, { query: { ...toQuery(def, { ...ts.query(), page: 1 }), format: 'csv' } });
    if (!sameSession(ctx, h)) return;
    const n = csvRowCount(await blob.text());
    saveBlob(blob, filename ?? fallbackCsvName('goodpayu-agent', def.csvName, todayIn(ctx.host.state.sh.config?.timezone)));
    ctx.gp.ui.toast(`${str(ctx.t, 'toastExport')} · ${n} rows`);
  } catch (e) {
    if (sameSession(ctx, h)) ctx.gp.ui.toast(ctx.gp.ui.err(e));
  } finally {
    h.exporting = false;
  }
}

export function openDetail(ctx: AgentCtx, page: TablePage, d: DetailSpec): void {
  ctx.host.setState({ modal: { type: 'detail', kind: str(ctx.t, page), title: d.title }, tb: { ...tbOf(ctx.host.state), detail: d } });
}

// 非表格页占位：pg.table=false 时模板不渲染这些键；键集合恒等于 KEYS
const IDLE: Readonly<Record<(typeof KEYS)[number], unknown>> = {
  cols: [], doPrimary: () => undefined, exportCsv: () => undefined, facets: [], hasLastReq: false, hasStats: false, hasTipBar: false, lastReq: '', loading: false,
  nextPage: () => undefined, nextStyle: '', noLastReq: true, onPageSize: () => undefined, onQd: {}, pageSize: '10', pages: [], prevPage: () => undefined, prevStyle: '',
  primaryAction: '', qd: { kw: '', from: '', to: '' }, resetQuery: () => undefined, rowCountText: '', rows: [], rowsEmpty: false, runQuery: () => undefined,
  searching: false, stats: [], tableBodyStyle: '', tabs: [], tipBar: '',
};

export const table: AgentDomain = {
  name: 'table',
  keys: KEYS,
  modalKeys: MODAL_KEYS,
  migrated: true,
  i18n: I18N_LISTS,
  rewrites: REWRITES_LISTS,
  state: () => ({ tb: { ...TB0 } }),
  mount(ctx) {
    // 业务请求只在 screen 事件后发（受限窗口 S-72 不触达）；进入表格页即按 A-00「导航切换」复位并拉第 1 页
    ctx.events.on('screen', ({ to }) => {
      if (isTablePage(to)) void resetTo(tableOf(ctx, to), PAGES[to].tabs[0] ?? '');
      // 06 A-06 统计卡（可提现 / 本月佣金）取 overview：进入佣金页即刷新，不沿用概览页时的旧值
      if (to === 'commissions') void loadOverview(ctx);
    });
    // 会话边界换代：session:lost（退出 / 过期 / 改密）与 session:ready（新会话进壳）各复位一次；
    // ready 一侧为纵深防御（会话代 epoch 已在各 lost 出口递增）
    ctx.events.on('session:lost', () => dropSession(ctx));
    ctx.events.on('session:ready', () => dropSession(ctx));
  },
  vals(ctx) {
    const { s, t, lang } = ctx;
    if (!isTablePage(s.page)) return { ...IDLE };
    const page = s.page;
    const def = PAGES[page];
    const ts = tableOf(ctx, page);
    const labels = strs(t, def.tabLabelKey);
    const stats = def.stats?.(ctx, ts.total) ?? [];
    const primary = def.primary?.(ctx) ?? null;
    const tip = def.tip?.(ctx) ?? '';
    const lastReq = ts.lastReq ? echoLine(def, ts.query()) : '';
    return {
      tabs: def.tabs.map((key, i) => ({ label: labels[i] ?? key, pick: () => void resetTo(ts, key), style: tabStyle(ts.tab === key) })),
      cols: def.cols.map((c) => {
        const sortable = !!c.sortKey;
        const active = sortable && ts.sortKey === c.sortKey;
        const arrow = sortable ? (active ? (ts.sortDir === 'desc' ? ' ↓' : ' ↑') : ' ⇅') : '';
        return {
          label: str(t, c.label) + arrow,
          sortable,
          notSortable: !sortable,
          // 02 §2.2：白名单外 sort 服务端 422，非白名单列不挂排序
          sort: () => {
            if (c.sortKey) void ts.toggleSort(c.sortKey);
          },
          style: thStyle(active, sortable),
        };
      }),
      rows: ts.rows.map((r) => {
        const open = () => openDetail(ctx, page, def.detail(ctx, r));
        const acts = def.actions?.(ctx, r) ?? [];
        return {
          cells: def.cells(ctx, r).map((c) => (c.chip ? chip(c.chip, c.v) : cell(c.v, c.sub ?? '', c.mono ?? false))),
          open,
          actions: acts.length ? acts.map((a) => act(a.label, () => a.run(ctx), a.danger ?? false)) : [act(str(t, 'aDetail'), open)],
        };
      }),
      rowsEmpty: ts.rows.length === 0 && !ts.loading,
      rowCountText: `${ts.total} ${str(t, 'records')} · ${lang === 'zh' ? '第' : 'page'} ${ts.pageNo}/${ts.totalPages}`,
      pageSize: String(ts.pageSize),
      onPageSize: (e: InputEvent) => void ts.setPageSize(Number.parseInt(e.target.value, 10)),
      pages: ts.pages.map((n) => ({ n, go: () => void ts.goPage(n), style: pageBtnStyle(n === ts.pageNo) })),
      prevPage: () => {
        if (ts.pageNo > 1) void ts.goPage(ts.pageNo - 1);
      },
      nextPage: () => {
        if (ts.pageNo < ts.totalPages) void ts.goPage(ts.pageNo + 1);
      },
      prevStyle: pagerStyle(ts.pageNo > 1),
      nextStyle: pagerStyle(ts.pageNo < ts.totalPages),
      qd: { kw: ts.draft.kw, from: ts.draft.from, to: ts.draft.to },
      onQd: {
        kw: (e: InputEvent) => ts.setDraft({ kw: e.target.value }),
        from: (e: InputEvent) => ts.setDraft({ from: e.target.value }),
        to: (e: InputEvent) => ts.setDraft({ to: e.target.value }),
      },
      // P-12：选项 = 服务端 facet_options（与当前页数据无关），下拉恒显示；首项「全部」由模板给出
      facets: def.cols
        .filter((c) => !!c.facet)
        .map((c) => {
          const param = c.facet ?? '';
          return {
            label: str(t, c.label),
            param,
            value: ts.draft.f[param] ?? '',
            options: (ts.facetOptions[param] ?? []).map((v) => ({ v, label: def.facetLabel ? def.facetLabel(t, param, v) : v })),
            onChange: (e: InputEvent) => ts.setDraft({ f: { [param]: e.target.value } }),
          };
        }),
      lastReq,
      hasLastReq: !!lastReq,
      noLastReq: !lastReq,
      runQuery: () => void ts.runQuery(),
      resetQuery: () => void ts.resetQuery(),
      searching: ts.searching,
      loading: ts.loading,
      tableBodyStyle: `overflow-x:auto;transition:opacity .2s;opacity:${ts.loading ? 0.45 : 1}`,
      hasStats: stats.length > 0,
      stats: stats.map((x) => statCard(x.label, x.value, x.sub, x.dark)),
      primaryAction: primary?.label ?? '',
      tipBar: tip,
      hasTipBar: tip !== '',
      doPrimary: () => primary?.run(ctx),
      exportCsv: () => void exportCsv(ctx, page),
    };
  },
  modal(ctx) {
    const d = tbOf(ctx.s).detail;
    const isDetail = ctx.s.modal?.type === 'detail' && d !== null;
    return {
      isDetail,
      fields: isDetail && d ? d.fields.map((f) => field(f.k, f.v, f.wide ?? false)) : [],
      hasList: isDetail && !!d?.list,
      listTitle: d?.list?.title ?? '',
      list: d?.list ? d.list.rows.map((r) => li(r.left, r.sub, r.right, r.rightSub)) : [],
      listEmpty: !!d?.list && d.list.rows.length === 0,
      actions: isDetail && d ? d.actions.map((a) => btn(a.label, () => a.run(ctx), a.kind)) : [],
    };
  },
};
